import { MAX_RECIPIENTS_COUNT } from '@constants/documents.constants';
import {
  Address,
  CreateIntegrationRequest,
  DocumentPatient,
  DocumentPatientDto,
  DocumentRecipientBundle,
  DocumentRecipientDto,
  DocumentStatus,
  DocumentVerificationStatus,
  DocumentVerificationTypes,
  IntegrationRequestDto,
  Medium,
  MediumList,
  NewDocumentPayload,
  PatientRecipientVerificationTypes,
  RecipientVerification,
  RecipientVerificationTypes,
  SendingRequest,
  SendingRequestDto,
  SourceType,
  Telecom,
  TelecomType,
  UploadDocumentJob,
} from '@domain/types';
import { getNameWithPrefix, isDocumentPatient, partitionArray } from '@domain/utils';
import { BASENAME } from '@launcher/constants';
import { getFileInMemory } from '@launcher/utils';
import { UserData } from '@store/user/user.state';

import { hasConnectorEnabled } from './integrations.util';

function getCreateDocumentBase({ job, user }: { job: UploadDocumentJob; user: UserData }) {
  const { sender, flowType } = job;
  return {
    sender,
    requesterId: user.uuid,
    file: getFileInMemory(job.fileStorageId) as File,
    flowType,
    /**
     * Monkeypath to fix https://honestica.atlassian.net/browse/CORE-3149
     * @todo please delete this after the creation of alphonse /v1/users/{id}/settings endpoint
     * don't forget to delete userSettings property in packages/server/api/validators/schemas/Document.json
     */
    userSettings: user.settings,
  };
}

export function getCreateIntegrationRequest({
  job,
  user,
}: {
  job: UploadDocumentJob;
  user: UserData;
}): CreateIntegrationRequest {
  return {
    ...getCreateDocumentBase({ job, user }),
    userSettings: user.settings,
  };
}

export const getUploadDocumentPayload = ({
  job,
  user,
}: {
  job: UploadDocumentJob;
  user: UserData;
}): NewDocumentPayload => ({
  ...getCreateDocumentBase({ job, user }),
  documentsMerged: [],
  isReadOnly: false,
});

export const isEmailDematPatient = (document: SendingRequest | SendingRequestDto) =>
  document.patient?.telecoms?.some((t) => t.type === TelecomType.EMAIL);

export const hasPostalAddress = (
  recipient: DocumentRecipientBundle | DocumentRecipientDto | DocumentPatientDto | DocumentPatient,
): boolean => recipient.addresses?.length > 0;

export const isPatientRecipient = (
  documentPatient: SendingRequest['patient'] | SendingRequestDto['patient'],
  recipients: DocumentRecipientBundle[],
): boolean =>
  !!(
    documentPatient &&
    recipients.find((rec) => {
      // Ignore patients with trustedContactRelationship as they are trusted contacts, not the patient themselves
      if (rec.trustedContactRelationship) {
        return false;
      }

      if (isDocumentPatient(documentPatient)) {
        return rec.id === documentPatient.patient.id && rec.type === 'Patient';
      }

      return rec.id === documentPatient.id && rec.type === 'Patient';
    })
  );

export const getPatientRecipient = (
  recipients: DocumentRecipientBundle[],
): DocumentRecipientBundle | undefined => recipients.find((rec) => rec.type === 'Patient');

const mediumHasDeliveryErrors = (medium: Medium<Telecom | Address>): boolean =>
  !!medium.deliveryStatus && medium.deliveryStatus.status === 'error';

const mediumHasDeliverySuccess = (medium: Medium<Telecom | Address>): boolean =>
  !!medium.deliveryStatus && medium.deliveryStatus.status === 'success';

const mediumHasDeliverySuccessOrRead = (medium: Medium<Telecom | Address>): boolean =>
  !!medium.deliveryStatus &&
  (medium.deliveryStatus.status === 'success' || medium.deliveryStatus.status === 'read');

export const recipientHasSomeMediumsInyError = (
  recipient: DocumentPatientDto | DocumentPatient | DocumentRecipientDto | DocumentRecipientBundle,
): boolean => {
  const telecomsOrAddressesHasDeliveryErrors = ({
    telecoms,
    addresses,
  }: {
    telecoms: MediumList<Telecom>;
    addresses: MediumList<Address>;
  }) =>
    [
      telecoms.some((medium) => mediumHasDeliveryErrors(medium)),
      addresses.some((medium) => mediumHasDeliveryErrors(medium)),
    ].some((predicate) => predicate);

  return telecomsOrAddressesHasDeliveryErrors(recipient);
};

export const recipientHasAllMediumsInError = (
  recipient: DocumentPatientDto | DocumentPatient | DocumentRecipientDto | DocumentRecipientBundle,
): boolean => {
  const telecomsInErrorList = recipient.telecoms.filter((medium) =>
    mediumHasDeliveryErrors(medium),
  );
  const addressesInErrorList = recipient.addresses.filter((medium) =>
    mediumHasDeliveryErrors(medium),
  );

  return (
    telecomsInErrorList.length === recipient.telecoms.length &&
    addressesInErrorList.length === recipient.addresses.length
  );
};

export const recipientHasAllMediumsInSuccess = (
  recipient: DocumentPatientDto | DocumentPatient | DocumentRecipientDto | DocumentRecipientBundle,
): boolean => {
  const telecomsInSuccessList = recipient.telecoms.filter((medium) =>
    mediumHasDeliverySuccessOrRead(medium),
  );
  const addressesInSuccessList = recipient.addresses.filter((medium) =>
    mediumHasDeliverySuccess(medium),
  );

  return (
    telecomsInSuccessList.length === recipient.telecoms.length &&
    addressesInSuccessList.length === recipient.addresses.length
  );
};

export const patientRecipientHasSomeMediumInError = (
  patientRecipient: DocumentRecipientBundle,
): boolean => (patientRecipient ? recipientHasSomeMediumsInyError(patientRecipient) : false);
export const patientRecipientHasAllMediumInError = (
  patientRecipient: DocumentRecipientBundle,
): boolean => (patientRecipient ? recipientHasAllMediumsInError(patientRecipient) : false);
export const patientRecipientHasAllMediumInSuccess = (
  patientRecipient: DocumentRecipientBundle,
): boolean => (patientRecipient ? recipientHasAllMediumsInSuccess(patientRecipient) : false);

export const orderRecipients = (
  recipients: DocumentRecipientBundle[],
  condition: (rec: DocumentRecipientBundle) => boolean,
) => {
  const partitioned = partitionArray(recipients ?? [], condition);
  return [...partitioned[0], ...partitioned[1]];
};

export const getTelecomsGroupedByTypes = (
  recipient: DocumentPatient | DocumentRecipientBundle,
): Map<TelecomType, Medium<Telecom>[]> => {
  const telecomsMap = new Map();
  recipient.telecoms.forEach((telecom) => {
    const collection = telecomsMap.get(telecom.type);

    if (!collection) {
      telecomsMap.set(telecom.type, [telecom]);
    } else {
      collection.push(telecom);
    }
  });
  return telecomsMap;
};

export const getMediumsForMultipleRecipients = (
  recipients: DocumentRecipientBundle[],
): { telecoms: MediumList<Telecom>; addresses: MediumList<Address> } => {
  const telecoms: MediumList<Telecom> = [];
  const addresses: MediumList<Address> = [];
  recipients.forEach((rec) => {
    telecoms.push(...rec.telecoms);
    addresses.push(...rec.addresses);
  });
  return { telecoms, addresses };
};

export const formatRecipientsForDashboard = (
  recipients: SendingRequest['recipients'],
  isPatientRec: boolean,
  orderCondition = (rec: DocumentRecipientBundle) => hasPostalAddress(rec),
): {
  trustedContactRecipients: DocumentRecipientBundle[];
  practitionersRecipients: DocumentRecipientBundle[];
  overflowRecipients?: DocumentRecipientBundle[];
} => {
  if (!recipients) {
    return {
      trustedContactRecipients: [],
      practitionersRecipients: [],
      overflowRecipients: undefined,
    };
  }

  // Extract all trusted contacts
  const allTrustedContacts = recipients.filter(
    (rec) => rec.type === 'Patient' && rec.trustedContactRelationship,
  );

  // Get ordered practitioners
  const orderedPractitioners = orderRecipients(
    recipients.filter((rec) => rec.type !== 'Patient'),
    orderCondition,
  );

  // Calculate total available slots
  const maxTotal = isPatientRec ? 4 : 5;
  let remainingSlots = maxTotal;

  // Reserve slot for patient
  if (isPatientRec) {
    remainingSlots -= 1;
  }

  // Distribute remaining slots between trusted contacts and practitioners
  const maxTrustedContactsSlots = Math.min(allTrustedContacts.length, remainingSlots);
  const trustedContactRecipients = allTrustedContacts.slice(0, maxTrustedContactsSlots);
  remainingSlots -= trustedContactRecipients.length;

  // Distribute remaining slots to practitioners
  const practitionersRecipients =
    remainingSlots > 0 ? orderedPractitioners.slice(0, remainingSlots) : [];

  // Calculate overflow (trusted contacts + practitioners that don't fit)
  const overflowTrustedContacts = allTrustedContacts.slice(maxTrustedContactsSlots);
  const overflowPractitioners = orderedPractitioners.slice(remainingSlots);
  const allOverflow = [...overflowTrustedContacts, ...overflowPractitioners];
  const overflowRecipients = allOverflow.length > 0 ? allOverflow : undefined;

  return { trustedContactRecipients, practitionersRecipients, overflowRecipients };
};

export function calculateRecipientCount(
  document: SendingRequest | SendingRequestDto | undefined,
): number {
  const count = 0;
  if (!document) {
    return count;
  }

  // Count recipient-context verifications as additional recipients
  const recipientContextVerifications = document.verificationsV2.filter(
    (verif) => verif.key === DocumentVerificationTypes.recipient_context && !verif.dismissedAt,
  );

  return document.recipients.length + recipientContextVerifications.length;
}

/**
 * Returns all selected telecoms for a given recipient
 */
export function getRecipientTelecomsFromDocument(
  document: SendingRequest | undefined,
  recipientId: string,
): MediumList<Telecom> {
  const recipient = document?.recipients.find((rec) => rec.entity?.id === recipientId);
  if (!recipient) return [];

  return recipient.telecoms;
}

/**
 * Returns all selected address for a given recipient
 */
export function getRecipientAddressesFromDocument(
  document: SendingRequest | undefined,
  recipientId: string,
): MediumList<Address> {
  const recipient = document?.recipients.find((rec) => rec.entity?.id === recipientId);
  if (!recipient) return [];

  return recipient.addresses;
}

export function isSuspended(
  documentDto: Pick<SendingRequestDto | IntegrationRequestDto, 'status'>,
): boolean {
  return documentDto.status === DocumentStatus.Suspended;
}

export function isReadyToSend({
  document,
}: {
  document: SendingRequest | SendingRequestDto;
}): boolean {
  // Status
  if (!(document.status === DocumentStatus.Suspended)) {
    return false;
  }

  // VerificationStatus
  if (
    ![
      DocumentVerificationStatus.ReadyToSend,
      DocumentVerificationStatus.UserVerificationNeeded,
      DocumentVerificationStatus.Downgraded,
    ].includes(document.verificationStatus)
  ) {
    return false;
  }

  // Verifications (will become useless with the new UserCorrectionNeeded status)
  const blockingVerifications = [
    DocumentVerificationTypes.integration_missing,
    DocumentVerificationTypes.patient_missing,
    DocumentVerificationTypes.recipients_missing,
    DocumentVerificationTypes.professional_recipients_missing,
    DocumentVerificationTypes.attachment_with_more_than_40_pages,
    DocumentVerificationTypes.recipient_context,
  ];
  const hasBlockingVerification = document.verificationsV2.find(
    (verification) => blockingVerifications.includes(verification.key) && !verification.dismissedAt,
  );

  if (hasBlockingVerification) {
    return false;
  }

  // Verifications (will become useless with the new UserCorrectionNeeded status)
  const blockingRecipientVerifications = [
    RecipientVerificationTypes.recipients_without_medium,
    PatientRecipientVerificationTypes.email_telecom_without_mfa,
    RecipientVerificationTypes.recipients_organization_multiple_mss_telecoms,
  ];

  const recipientsVerifications = document.recipients.reduce((acc, curr) => {
    acc.push(...curr.verifications);
    return acc;
  }, [] as RecipientVerification[]);

  const hasBlockingRecipientVerification = recipientsVerifications.find(
    (verification) =>
      blockingRecipientVerifications.includes(verification.key) && !verification.dismissedAt,
  );

  if (hasBlockingRecipientVerification) {
    return false;
  }

  // Sender
  if (!document.sender) {
    return false;
  }

  // Patient
  if (!document?.patient) {
    return false;
  } // why should we need patient from entities?

  // Recipient count
  const recipientsCount = calculateRecipientCount(document);

  return recipientsCount <= MAX_RECIPIENTS_COUNT;
}

// Relic of Redux, might be deprecated
export function isPatientWithGam(sendingRequest: SendingRequest) {
  return !!(
    sendingRequest?.integration &&
    sendingRequest.patient?.ehrPatient &&
    sendingRequest.patient.patient?.ehrDataOverwritten
  );
}

export function areSomeConnectorsEnabledForSending(
  document: SendingRequestDto | undefined,
): boolean {
  return hasConnectorEnabled(document?.integration);
}

export function isReadyToBatchSend({
  document,
}: {
  document: SendingRequest | SendingRequestDto;
}): boolean {
  if (!isReadyToSend({ document })) {
    return false;
  }

  if (document.verificationStatus !== DocumentVerificationStatus.ReadyToSend) {
    return false;
  }

  return true;
}

export function documentIdIsReadyToSend({
  documentId,
  documents,
}: {
  documentId: string;
  documents?: SendingRequest[] | SendingRequestDto[];
}): boolean {
  const document = documents?.find((doc) => doc.id === documentId);

  if (!document) {
    return false;
  }

  return isReadyToBatchSend({ document });
}

export function isMissingRequestedProfessionalRecipients({
  document,
}: {
  document: SendingRequest | SendingRequestDto;
}): boolean {
  return document.verificationsV2.some(
    (verification) =>
      verification.key === DocumentVerificationTypes.professional_recipients_missing,
  );
}

export function hasAttachmentWithMoreThan40Pages({
  document,
}: {
  document: SendingRequest | SendingRequestDto;
}): boolean {
  return document.verificationsV2.some(
    (verification) =>
      verification.key === DocumentVerificationTypes.attachment_with_more_than_40_pages,
  );
}

export const getDocumentRecipientName = (document: SendingRequest) => {
  let recipientName =
    document.recipients.length > 0 && document.recipients[0].entity
      ? getNameWithPrefix(document.recipients[0].entity)
      : undefined;
  const { integration } = document;

  if (integration) {
    recipientName = recipientName ?? integration.name;
  }
  return recipientName;
};

export const getSourceDisplayI18nKey = (sourceType: SourceType | undefined): string | undefined => {
  if (!sourceType) {
    return undefined;
  }

  switch (sourceType) {
    case SourceType.HL7_MDM_CDA:
      return 'source.hl7_mdm_cda';
    case SourceType.HL7_ORU_CDA:
      return 'source.hl7_oru_cda';
    case SourceType.HL7_ORU_PDF:
      return 'source.hl7_oru_pdf';
    case SourceType.IN_PDF:
      return 'source.in_pdf';
    case SourceType.FORWARD:
      return 'source.forward';
    case SourceType.LIFEN_DOCUMENT_FORWARD:
      return 'source.forward_lifen_document';
    case SourceType.LIFEN_INTEGRATION_FORWARD:
      return 'source.forward_lifen_integration';
    case SourceType.IV:
      return 'source.iv';
    default:
      return 'source.iv';
  }
};

export type AddPatientRecipientFlowParams = {
  isAddressSelected: boolean;
  isTelecomSelected: boolean;
  isEnsSelected: boolean;
};

export const selectPathName = () => {
  const { pathname } = window.location;
  return pathname.replace(BASENAME, '');
};

export const getAddPatientRecipientFlowParams = (): AddPatientRecipientFlowParams | undefined => {
  const { search } = window.location;
  const searchParams = new URLSearchParams(search);

  const isAddressSelected = searchParams.get('isAddressSelected') as string;
  const isTelecomSelected = searchParams.get('isTelecomSelected') as string;
  const isEnsSelected = searchParams.get('isEnsSelected') as string;

  if (!isAddressSelected && !isTelecomSelected && !isEnsSelected) {
    return undefined;
  }

  return {
    isAddressSelected: isAddressSelected === 'true',
    isTelecomSelected: isTelecomSelected === 'true',
    isEnsSelected: isEnsSelected === 'true',
  };
};

export const CDA_SOURCE_TYPES = [SourceType.HL7_MDM_CDA, SourceType.HL7_ORU_CDA] as const;

// Structural: v2's SourceV2.type is a plain `string` (see sendingRequestsV2.types.ts), not the
// SourceType enum — both stay assignable without a cast at call sites.
export const isCdaDocument = (sourceType?: string): boolean => {
  return CDA_SOURCE_TYPES.includes(sourceType as SourceType.HL7_MDM_CDA | SourceType.HL7_ORU_CDA);
};
